import type { IconName } from '@forecastify/ui';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ HOW A PERSON AND A LEAVE TYPE LOOK, in one place for every leave screen.│
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE IDENTITY HALF MOVED TO `identity.ts` — C68.** `identityColor` and `initialsOf`
 * were here because the leave screens needed them first, and then eleven other screens imported
 * them from `screens/leave/`, which is not where a shared rule lives. What is left is the one
 * rule that IS about leave:
 *
 * ⚠️⚠️ **AN AVATAR WITHOUT A COLOUR IS A GREY DISC, AND EVERY ROW'S IS THE SAME GREY.** `members`
 * carries no colour column — the mockup's `U[uid].c` has no counterpart here — so the identity hue
 * is derived from the member id against the design system's `--id-1 … --id-8`, which exist for
 * exactly this: *"label projects, clients and people by identity, never by status"*. Derived rather
 * than stored means the same person is the same colour on every screen without a migration.
 *
 * ⚠️ **A LEAVE TYPE'S ICON IS FREE TEXT IN THE DATABASE AND A CLOSED SET IN THE UI.** `leave_types.
 * icon` takes any string; `Icon` accepts sixty names. A type seeded with `heart-pulse` — which
 * lucide has and our vendored subset does not — rendered NOTHING, which is how Betegszabadság lost
 * its icon. This resolves the string to a name the component knows, and falls back rather than
 * disappearing.
 */

/**
 * ⚠️ The names our `Icon` actually ships, mapped from the ones a leave type is likely to carry.
 * Anything unknown becomes `sun`, which is the vocabulary's own default.
 */
const KNOWN_ICONS: Readonly<Record<string, IconName>> = {
  sun: 'sun',
  moon: 'moon',
  home: 'home',
  house: 'home',
  timer: 'timer',
  clock: 'clock',
  'shield-check': 'shield-check',
  'heart-pulse': 'shield-check',
  heart: 'shield-check',
  'circle-minus': 'moon',
  'calendar-days': 'calendar-days',
  briefcase: 'folder',
  plane: 'sun',
  /* ⚠️ The twenty the settings picker offers, each mapping to itself — a symbol somebody CHOSE
     must survive the round trip, and falling back to `sun` would silently undo the choice. */
  calendar: 'calendar',
  building: 'building',
  users: 'users',
  'user-round': 'user-round',
  star: 'star',
  globe: 'globe',
  flag: 'flag',
  sparkles: 'sparkles',
  'circle-dot': 'circle-dot',
  'file-text': 'file-text',
  inbox: 'inbox',
  key: 'key',
  monitor: 'monitor',
};

/**
 * ⚠️ **The symbols a leave type may be given** — the mockup's `LEAVE_SYMBOLS`, and every one of them
 * is a name `Icon` actually ships, so the picker cannot offer a glyph that renders as nothing.
 */
export const LEAVE_SYMBOLS: readonly IconName[] = [
  'sun',
  'moon',
  'calendar',
  'calendar-days',
  'clock',
  'timer',
  'home',
  'building',
  'users',
  'user-round',
  'shield-check',
  'star',
  'globe',
  'flag',
  'sparkles',
  'circle-dot',
  'file-text',
  'inbox',
  'key',
  'monitor',
];

export function leaveIcon(icon: string | null | undefined): IconName {
  if (icon === null || icon === undefined) return 'sun';
  return KNOWN_ICONS[icon] ?? 'sun';
}
